import React, { Fragment, useMemo } from 'react'
import { FeatureFlag } from '@openfeature/react-sdk'
import { every, find, map, negate, prop, propEq, uniq } from 'lodash/fp'
import { ChevronDownIcon, CopyIcon, Link2Icon, Trash2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { BulkDeletePricingCampaigns } from '@/features/pricing-campaign/bulk-delete'
import { CopyPricingCampaignSettings } from '@/features/pricing-campaign/copy-settings'
import { CopyPricingCampaign } from '@/features/pricing-campaign/copy-to-og'
import { ReassignPricingCampaign } from '@/features/pricing-campaign/reassign-to-og'

import { Access } from '@/entities/session'

import { Button } from '@/shared/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { ampli } from '@/shared/lib/ampli'
import { useMutationObservers } from '@/shared/lib/useMutationObservers'

import { usePricingCampaigns } from '../providers/PricingCampaignsProvider'
import AssignToScenarioDropdown from './AssignToScenarioDropdown'

export const PricingCampaignBulkActions = () => {
	const { t } = useTranslation()

	const { data, selected, setSelected } = usePricingCampaigns()
	useMutationObservers({
		mutationKey: ['pricing-campaign-bulk-delete'],
		onSuccess: () => {
			setSelected([])
		},
	})
	const selectedOptimizationGroupsIds = useMemo((): string[] => {
		const pcs = selected?.map((id) => find(propEq('campaign.id', id), data))
		const groupIds = map(prop('campaign.optimization_group_id'), pcs)

		return uniq(groupIds)
	}, [selected, data])
	const isOnlyLocalSelected = useMemo(
		() =>
			selectedOptimizationGroupsIds.every(Boolean) &&
			selectedOptimizationGroupsIds?.length === 1,
		[selectedOptimizationGroupsIds]
	)
	const isOnlyGlobalSelected = useMemo(() => {
		const pcs = selected?.map((id) => find(propEq('campaign.id', id), data))
		const groupIds = map(prop('campaign.optimization_group_id'), pcs)
		const globalPcSetIds = map(prop('campaign.global_pc_set_id'), pcs)

		return (
			groupIds.every(negate(Boolean)) &&
			uniq(groupIds)?.length === 1 &&
			every(propEq('is_main', true), globalPcSetIds)
		)
	}, [selected, data])

	return (
		<Fragment>
			<FeatureFlag flagKey='core_whatif' defaultValue={true}>
				{isOnlyLocalSelected && (
					<AssignToScenarioDropdown
						optimizationGroupId={selectedOptimizationGroupsIds[0]}
					/>
				)}
			</FeatureFlag>
			{isOnlyLocalSelected && selected?.length === 1 && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button>
							{t('pc.action.copy_or_reassign', {
								defaultValue: 'Copy/reassign PCs',
							})}
							<ChevronDownIcon className='shrink-0 opacity-50' />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent>
						<CopyPricingCampaign pricingCampaignsIds={selected}>
							<DropdownMenuItem onClick={() => ampli.pcCopyClicked()}>
								<CopyIcon />
								{t('pc.action.copy', { defaultValue: 'Copy' })}
							</DropdownMenuItem>
						</CopyPricingCampaign>
						<CopyPricingCampaignSettings
							pricingCampaignId={selected[0]}
							onDone={() => setSelected([])}
						>
							<DropdownMenuItem onClick={() => ampli.pcCopySettingsClicked()}>
								<CopyIcon />
								{t('pc.action.copy_settings', {
									defaultValue: 'Copy settings',
								})}
							</DropdownMenuItem>
						</CopyPricingCampaignSettings>
						<ReassignPricingCampaign
							pricingCampaignsIds={selected}
							onDone={() => setSelected([])}
						>
							<DropdownMenuItem onClick={() => ampli.pcReassignClicked()}>
								<Link2Icon />
								{t('pc.action.reassign', { defaultValue: 'Reassign' })}
							</DropdownMenuItem>
						</ReassignPricingCampaign>
					</DropdownMenuContent>
				</DropdownMenu>
			)}
			{isOnlyLocalSelected && selected?.length > 1 && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button>
							{t('pc.action.copy_or_reassign', {
								defaultValue: 'Copy/reassign PCs',
							})}
							<ChevronDownIcon className='shrink-0 opacity-50' />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent>
						<CopyPricingCampaign pricingCampaignsIds={selected}>
							<DropdownMenuItem onClick={() => ampli.pcCopyClicked()}>
								<CopyIcon />
								{t('pc.action.copy', { defaultValue: 'Copy' })}
							</DropdownMenuItem>
						</CopyPricingCampaign>

						<ReassignPricingCampaign
							pricingCampaignsIds={selected}
							onDone={() => setSelected([])}
						>
							<DropdownMenuItem onClick={() => ampli.pcReassignClicked()}>
								<Link2Icon />
								{t('pc.action.reassign', { defaultValue: 'Reassign' })}
							</DropdownMenuItem>
						</ReassignPricingCampaign>
					</DropdownMenuContent>
				</DropdownMenu>
			)}
			{isOnlyLocalSelected && (
				<BulkDeletePricingCampaigns pricingCampaignsIds={selected}>
					<Button>
						<Trash2Icon />
						{t('pc.action.bulk_delete', { value: selected?.length })}
					</Button>
				</BulkDeletePricingCampaigns>
			)}
			{isOnlyGlobalSelected && selected?.length > 0 && (
				<Access permissions={['DELETE_GLOBAL_PRICING_CAMPAIGNS']}>
					<BulkDeletePricingCampaigns pricingCampaignsIds={selected}>
						<Button>
							<Trash2Icon />
							{t('pc.action.bulk_delete', { value: selected?.length })}
						</Button>
					</BulkDeletePricingCampaigns>
				</Access>
			)}
		</Fragment>
	)
}
